import axios from "axios";
import { toast } from "sonner";
import {verifiedMode,modeEpoch,setVerifiedMode,customerPreviewToken,rememberCustomerToken} from './workspaceMode';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});
api.interceptors.request.use(config=>{
  config.headers=config.headers||{};
  const pageToken=new URLSearchParams(window.location.search).get('token');
  if(pageToken&&config.url==='/env/public-status')config.params={...config.params,token:pageToken};
  config.__modeEpoch=modeEpoch();
  config.__expectedMode=verifiedMode();
  if(config.__expectedMode)config.headers['X-Culinex-Env']=config.__expectedMode;
  const hasOrderToken=!!config.params?.token||/[?&]token=/.test(config.url||'');
  const preview=hasOrderToken?'':customerPreviewToken();
  if(preview)config.headers['X-Culinex-Customer-Preview']=preview;
  return config;
});
api.interceptors.response.use(response=>{
  const config=response.config||{},incoming=response.headers?.['x-culinex-env'];
  const authority=/\/env\/(status|public-status|set)$/.test(config.url||'');
  const customerLink=!!config.params?.token&&(/\/customer-payments\//.test(config.url||'')||/\/orders\/track\//.test(config.url||''));
  if(!authority&&config.__modeEpoch!==modeEpoch()&&(config.__expectedMode||incoming!==verifiedMode()))throw Error('Workspace changed. Refresh this page before continuing.');
  if(incoming&&(authority||customerLink||!verifiedMode()))setVerifiedMode(incoming);
  rememberCustomerToken(response.data);
  rememberCustomerToken(response.data?.order);
  return response;
},error=>{
  const detail=error?.response?.data?.detail;
  if(detail?.workspace_changed&&detail.env)setVerifiedMode(detail.env);
  return Promise.reject(error);
});

// Phase 124-C · Concurrency Guard — surface stale-write conflicts as a
// friendly toast instead of a silent overwrite. Backend replies with
// HTTP 409 + { detail: { message, current_version, updated_by } } when the
// caller's expected_version no longer matches the record's real version.
api.interceptors.response.use(
  (r) => r,
  (err) => {
    try {
      const d = err?.response?.data?.detail;
      if (err?.response?.status === 409 && d && typeof d === "object" && d.concurrency) {
        toast.error(d.message || "This record was changed by someone else.", {
          description: d.updated_by ? `Last edited by ${d.updated_by}. Reloading fresh data…` : "Reloading fresh data…",
          duration: 6000,
        });
      }
    } catch {}
    return Promise.reject(err);
  },
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// CulinexOS · Canonical Data Formatting Layer
// Keep operational/financial number presentation consistent across the app.

export const inr = (n) => {
  const value = Number(n || 0);
  return "\u20B9" + value.toLocaleString("en-IN", {
    minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
    maximumFractionDigits: 2,
  });
};

export const inr0 = (n) => {
  const value = Number(n || 0);
  return "\u20B9" + value.toLocaleString("en-IN", {
    maximumFractionDigits: 0,
  });
};

export const number = (n, decimals = 0) => {
  const value = Number(n || 0);
  return value.toLocaleString("en-IN", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

export const percent = (n, decimals = 1) => {
  const value = Number(n || 0);
  return `${value.toLocaleString("en-IN", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}%`;
};

export const quantity = (n, decimals = 3) => {
  const value = Number(n || 0);
  return value.toLocaleString("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  });
};

export const compactInr = (n) => {
  const value = Number(n || 0);

  if (Math.abs(value) >= 10000000) {
    return `₹${(value / 10000000).toFixed(2)}Cr`;
  }

  if (Math.abs(value) >= 100000) {
    return `₹${(value / 100000).toFixed(2)}L`;
  }

  if (Math.abs(value) >= 1000) {
    return `₹${(value / 1000).toFixed(1)}K`;
  }

  return inr0(value);
};

export const dateTime = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";

  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const resolveImg = (u) => {
  if (!u) return "";
  if (typeof u !== "string") return "";
  if (u.startsWith("http")) return u;
  if (u.startsWith("/api/")) return `${BACKEND_URL}${u}`;
  return u;
};

export default api;
